import React from 'react'
import PropTypes from 'prop-types'
import { Spinner } from 'react-bootstrap'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMachine } from '@xstate/react'
import {
  ContentContainer,
  FooterContainer,
  InstructionsContainer,
  InstructionsText,
  StyledHeading,
  StyledParagraph,
} from '../styled'
import interviewMachine from '../interviewMachine'
import apiInterviewVersions from '../../services/api/interviewVersion'
import { getRawJWTFromToken } from '../../utilities/tokenUtils'
import storageApi from '../../services/firebaseStorage'
import theme from '../../constants/theme'
import PrimaryButton from '../buttons/PrimaryButton'
import { DeviceControl } from './DeviceControl'
import useDeviceType from '../../hooks/useDeviceType'

const ButtonActions = {
  RESCHEDULE_INTERVIEW: 'rescheduleInterview',
  BEGIN_INTERVIEW: 'beginInterview',
  PERFORM_DEVICE_CHECK: 'performTechCheck',
  STOP_DEVICE_CHECK: 'stopTechCheck',
}

const getButtonSettings = ({
  isTested,
  loading,
  reschedule,
  isFinished,
  isRecording,
  buttonsFunctions,
  t,
  isMobile,
}) => ({
  [ButtonActions.RESCHEDULE_INTERVIEW]: {
    isVisible: isTested && reschedule && !isFinished,
    color: theme.talentinoSpecialBlue,
    backgroundcolor: theme.talentinoWhiteBlue,
    hovercolor: theme.talentinoDarkerWhiteBlue,
    onClick: buttonsFunctions.rescheduleInterview,
    text: t('preInterview.rescheduleInterview'),
    width: isMobile ? '140px' : '160px',
    style: {
      fontSize: '12px',
      padding: isMobile ? '5px 10px' : '5px 40px',
      whiteSpace: 'nowrap',
    },
  },
  [ButtonActions.BEGIN_INTERVIEW]: {
    isVisible: isTested && !loading,
    onClick: buttonsFunctions.handleSubmit,
    text: t('preInterview.startInterview'),
    width: isMobile ? '140px' : '160px',
    style: {
      fontSize: '12px',
      padding: isMobile ? '5px 10px' : '5px 40px',
      whiteSpace: 'nowrap',
    },
  },
  [ButtonActions.PERFORM_DEVICE_CHECK]: {
    isVisible: !isTested && !loading,
    onClick: buttonsFunctions.handleStartRecording,
    text: t('preInterview.test'),
    width: isMobile ? '140px' : '160px',
    style: {
      fontSize: '12px',
      padding: isMobile ? '5px 10px' : '5px 40px',
      whiteSpace: 'nowrap',
    },
  },
  [ButtonActions.STOP_DEVICE_CHECK]: {
    isVisible: loading,
    color: !isRecording && theme.talentinoSpecialBlue,
    backgroundcolor: isRecording
      ? theme.talentinoRed
      : theme.talentinoWhiteBlue,
    hovercolor: isRecording
      ? theme.talentinoDarkerRed
      : theme.talentinoDarkerWhiteBlue,
    onClick: buttonsFunctions.handleStopRecording,
    text: isRecording ? t('preInterview.stopTesting') : 'Getting results...',
    disabled: !isRecording && loading,
    width: isMobile ? '140px' : '160px',
    style: {
      fontSize: '12px',
      padding: isMobile ? '5px 10px' : '5px 40px',
      whiteSpace: 'nowrap',
    },
  },
})

function InterviewPrepFooter(props) {
  const { t } = useTranslation()
  const reschedule = true
  const buttonSettings = getButtonSettings({ ...props, t, reschedule })

  return (
    <FooterContainer isMobile={props.isMobile}>
      {Object.keys(ButtonActions).map((action) => {
        const { isVisible, color, width, style, ...buttonProps } =
          buttonSettings[ButtonActions[action]]
        return (
          isVisible && (
            <PrimaryButton
              key={ButtonActions[action]}
              color={color || undefined}
              width={width}
              style={style}
              {...buttonProps}
            >
              {buttonProps.text}
            </PrimaryButton>
          )
        )
      })}
    </FooterContainer>
  )
}

InterviewPrepFooter.propTypes = {
  isMobile: PropTypes.bool.isRequired,
}

const initialDeviceState = {
  hasMicPermission: false,
  hasCameraPermission: false,
  isMicEnabled: false,
  isCameraEnabled: false,
  isRecording: false,
  permissionError: '',
}

function TestVideo({ rescheduleInterview, rescheduleEnabled, isFinished }) {
  const { t } = useTranslation()
  const { interviewToken } = useParams()
  const rawJWT = getRawJWTFromToken(interviewToken)
  const [, send] = useMachine(interviewMachine, {
    services: {
      fetchInterview: () =>
        apiInterviewVersions.startInterview(rawJWT).then((res) => res),
    },
  })
  const navigate = useNavigate()
  const [deviceState, setDeviceState] = React.useState(initialDeviceState)
  const [recordedVideo, setRecordedVideo] = React.useState(null)
  const deviceType = useDeviceType()
  const isMobile = deviceType !== 'desktop'

  const videoRef = React.useRef(null)
  const mediaRecorderRef = React.useRef(null)
  const streamRef = React.useRef(null)

  const [isRecording, setIsRecording] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [isTested, setIsTested] = React.useState(false)

  const [message, setMessage] = React.useState(t('preInterview.testDevices'))
  const [secondMessage, setSecondMessage] = React.useState(
    t('preInterview.testCameraAndMic'),
  )

  const testUploadSpeed = async () => {
    setMessage(t('preInterview.testingUploadSpeed'))

    const createFile = () => {
      const size = 1024 * 1024
      const char = 'a'
      const content = char.repeat(size)
      const file = new Blob([content], { type: 'text/plain' })
      return file
    }

    const testFile = createFile()

    const testFilePath = `speed-test/${testFile.filename}`
    const speedInMbps = await storageApi.uploadTest(testFile, testFilePath)

    setMessage(`${speedInMbps.toFixed(2)} Mbps`)

    if (speedInMbps < 1) {
      setSecondMessage(t('preInterview.lowUploadSpeed'))
    } else if (speedInMbps < 8) {
      setSecondMessage(t('preInterview.mediumUploadSpeed'))
    } else {
      setSecondMessage(t('preInterview.acceptedUploadSpeed'))
    }

    let SpeedCategory

    if (speedInMbps > 8) {
      SpeedCategory = 'high'
    } else if (speedInMbps > 1) {
      SpeedCategory = 'medium'
    } else {
      SpeedCategory = 'low'
    }

    const data = {
      speed_value: speedInMbps,
      speed_category: SpeedCategory,
    }

    await apiInterviewVersions
      .logUploadSpeed({ interviewToken: rawJWT, data })
      .then(() => {
        setLoading(false)
        setIsTested(true)
      })
  }

  const handleStartRecording = () => {
    setLoading(true)
    setMessage('Testing...')
    setSecondMessage('')
    setIsRecording(true)
    startRecording()
  }
  const handleStopRecording = () => {
    setIsRecording(false)
    stopRecording()
    testUploadSpeed()
  }
  const startStream = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: deviceState.isCameraEnabled
          ? {
              deviceId: deviceState.selectedCamera || undefined,
            }
          : false,
        audio: deviceState.isMicEnabled
          ? {
              deviceId: deviceState.selectedMicrophone || undefined,
            }
          : false,
      })

      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Stream error:', error)
      setDeviceState((prev) => ({
        ...prev,
        permissionError: 'Failed to access media devices',
      }))
    }
  }

  const stopStream = (stream) => {
    const currentStream = stream || streamRef.current
    if (currentStream) {
      currentStream.getTracks().forEach((track) => track.stop())
    }
    if (streamRef.current) {
      streamRef.current = null
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
  }

  const startRecording = async () => {
    try {
      if (!streamRef.current) {
        await startStream()
      }
      setRecordedVideo(null)

      const chunks = []

      const stream = await navigator.mediaDevices.getUserMedia({
        video: deviceState.isCameraEnabled
          ? {
              deviceId: deviceState.selectedCamera || undefined,
            }
          : false,
        audio: deviceState.isMicEnabled
          ? {
              deviceId: deviceState.selectedMicrophone || undefined,
            }
          : false,
      })

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'video/webm;codecs=vp8,opus',
      })

      mediaRecorderRef.current = mediaRecorder

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunks.push(event.data)
        }
      }

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/webm' })
        const url = URL.createObjectURL(blob)
        setRecordedVideo(url)

        stream.getTracks().forEach((track) => track.stop())
      }

      mediaRecorder.start(1000)
      setDeviceState((prev) => ({ ...prev, isRecording: true }))
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error starting recording:', error)
      setDeviceState((prev) => ({
        ...prev,
        permissionError: 'Failed to start recording',
      }))
    }
  }

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== 'inactive'
    ) {
      mediaRecorderRef.current.stop()
      setDeviceState((prev) => ({ ...prev, isRecording: false }))
    }
  }

  React.useEffect(() => {
    if (deviceState.isCameraEnabled || deviceState.isMicEnabled) {
      startStream()
    } else {
      stopStream()
    }
  }, [
    deviceState.isCameraEnabled,
    deviceState.isMicEnabled,
    deviceState.selectedCamera,
    deviceState.selectedMicrophone,
  ])

  const handleSubmit = () => {
    localStorage.setItem('isReady', 'true')
    send('startInterview')
    navigate(0)
  }

  const buttonsFunctions = {
    rescheduleInterview,
    handleSubmit,
    handleStartRecording,
    handleStopRecording,
  }

  return (
    <>
      <InstructionsContainer>
        <InstructionsText style={{ fontSize: isMobile ? '16px' : '18px' }}>
          {t('preInterview.testDevicesTitle')}
        </InstructionsText>
      </InstructionsContainer>
      <ContentContainer
        padding={isMobile ? '15px 10px' : '30px'}
        style={{ gap: isMobile ? '10px' : '20px' }}
      >
        <DeviceControl
          deviceState={deviceState}
          setDeviceState={setDeviceState}
          recordedVideo={recordedVideo}
          videoRef={videoRef}
          stopStream={startStream}
        />
        <StyledHeading
          style={{
            fontSize: isMobile ? '16px' : '18px',
            marginTop: isMobile ? '10px' : '24px',
            marginBottom: isMobile ? '5px' : '10px',
          }}
        >
          {message}
        </StyledHeading>
        <StyledParagraph
          color={isTested && theme.talentinoDarkerGray}
          style={{
            fontSize: isMobile ? '14px' : '16px',
            marginBottom: isMobile ? '5px' : '15px',
          }}
        >
          {secondMessage}
          {loading && (
            <Spinner
              animation="border"
              variant="secondary"
              size={isMobile ? 'sm' : 'md'}
            />
          )}
        </StyledParagraph>

        <InterviewPrepFooter
          isTested={isTested}
          loading={loading}
          isRecording={isRecording}
          rescheduleEnabled={rescheduleEnabled}
          isFinished={isFinished}
          buttonsFunctions={buttonsFunctions}
          isMobile={isMobile}
        />
      </ContentContainer>
    </>
  )
}

export default TestVideo

TestVideo.propTypes = {
  rescheduleInterview: PropTypes.func,
  rescheduleEnabled: PropTypes.bool,
  isFinished: PropTypes.bool,
}

TestVideo.defaultProps = {
  rescheduleInterview: () => {},
  rescheduleEnabled: false,
  isFinished: false,
}
